import { useContext, useEffect, useRef, useState } from 'react';
import { useJwt } from 'react-jwt';
import { useHistory, useLocation } from 'react-router-dom';

import { LDFlags } from '../../constants/flags';
import { DASHBOARD } from '../../constants/urls';
import { useHasAnyDealLazyQuery, useTemporaryInfoLazyQuery } from '../../gql/generated/graphql';
import { useFlag } from '../../hooks';
import { CookieKeys, clearCookie, useCookie } from '../../hooks/useCookie';
import useOptOutLookup from '../../hooks/useOptOutLookup';
import { ResetWsContext } from '../../providers/AuthorizedApolloProvider';
import hotjar from '../../services/analytics/hotjar';
import { identify } from '../../services/analytics/rudderstack';
import { parseTokenFromHash } from '../../services/auth0';
import { logger } from '../../services/sentry';
import { JWTToken, clearOneClickLoginAttempts, getEmailFromJWT } from '../../utils/auth';
import OneClickLogin from './OneClickLogin';
import PasswordLogin from './PasswordLogin';

const AccountLogin = () => {
  const passwordlessDashboardAccessEnabled = useFlag(LDFlags.PASSWORDLESS_DASHBOARD_ACCESS);
  const location = useLocation<{ passedEmail?: string }>();
  const emailFromState = location.state?.passedEmail || '';
  const [showOneClickLogin, setShowOneClickLogin] = useState(passwordlessDashboardAccessEnabled);
  const [passedEmail, setPassedEmail] = useState(emailFromState);
  const [hashParseError, setHashParseError] = useState<string | false>(false);

  const { resetWebSocket } = useContext(ResetWsContext);
  const [accessToken, setAccessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const { decodedToken, isExpired } = useJwt<JWTToken>(accessToken || '');
  const history = useHistory();
  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);

  const [getTempInfo] = useTemporaryInfoLazyQuery({
    context: { isErrorHandled: true },
  });

  // Fetched lazily instead of eagerly on mount to avoid the "Invalid session"
  // error racing the auth callback hash parse and redirecting to MKT_HOME.
  // Clears the GUID cookie if the temp info is expired or imported.
  const getTempLastUrl = async (): Promise<string | undefined> => {
    if (!guid) return undefined;

    try {
      const { data } = await getTempInfo({
        fetchPolicy: 'network-only',
        variables: { id: guid, allowImported: false },
      });

      const info = data?.temporaryInfo;
      if (info?.id && info.data?.vin) {
        hotjar.identify(info.data.vin, {
          temp_info_id: info.id,
          state: info.data?.state || undefined,
        });
      }
      return info?.data?.last_url ?? undefined;
    } catch (error) {
      logger.error('AccountLogin.tsx', 'Error fetching temp info', error, null);
      clearCookie(CookieKeys.GUID_KEY);
      return undefined;
    }
  };

  const checkOptOut = useOptOutLookup();
  const [checkHasAnyDeal] = useHasAnyDealLazyQuery({
    fetchPolicy: 'network-only',
  });

  const handleAuthenticated = async () => {
    const lastTempUrl = await getTempLastUrl();

    try {
      const { data: hasAnyDeal } = await checkHasAnyDeal({
        variables: { email: getEmailFromJWT(decodedToken) || '' },
      });

      if (hasAnyDeal?.hasAnyDeal) {
        history.replace(DASHBOARD);
        return;
      }
    } catch (error) {
      logger.error('AccountLogin.tsx', '', error, null);
    }

    history.replace(lastTempUrl || DASHBOARD);
  };

  useEffect(() => {
    if (!!decodedToken && !isExpired) {
      checkOptOut({ auth0_id: decodedToken.sub, email: getEmailFromJWT(decodedToken) }).then(() => {
        identify({ auth0_id: decodedToken.sub });
        handleAuthenticated();
      });
    }
  }, [decodedToken, isExpired]);

  const alreadyRan = useRef(false);
  useEffect(() => {
    if (!window.location.hash || alreadyRan.current) {
      return;
    }

    alreadyRan.current = true;

    // Pass the state from the hash directly so Auth0 still does full token
    // validation (signature, expiry) but doesn't require the state to exist
    // in localStorage — which it won't on cross-device magic link opens.
    const hashState =
      new URLSearchParams(window.location.hash.replace('#', '?')).get('state') || undefined;

    parseTokenFromHash({
      hash: window.location.hash,
      state: hashState,
      callback: (err, authResult) => {
        if (err || !authResult?.accessToken) {
          logger.error('AccountLogin.tsx', 'parseTokenFromHash failed', err, null);

          setHashParseError(
            'We were unable to sign you in. This can happen if a link was already used or has expired. Please request a new one-time link below.',
          );
          return;
        }
        resetWebSocket();
        clearOneClickLoginAttempts();
        setAccessToken(authResult.accessToken);
      },
    });
  }, [window.location.hash]);

  return showOneClickLogin ? (
    <OneClickLogin
      setShowOneClickLogin={(value) => {
        setShowOneClickLogin(value);
        setHashParseError(false);
      }}
      passedEmail={passedEmail}
      initialError={hashParseError || undefined}
    />
  ) : (
    <PasswordLogin setShowOneClickLogin={setShowOneClickLogin} setPassedEmail={setPassedEmail} />
  );
};

export default AccountLogin;
